import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppConfig } from '../../hooks/AppConfigContext';

interface FooterProps {
  /**
   * Rendered inside the mobile results list drawer rather than the app shell.
   * The shell-level footer hides itself on mobile results (see below), so this
   * flag lets the drawer copy still render there.
   */
  embedded?: boolean;
}

const Footer = ({ embedded = false }: FooterProps) => {
  const { config } = useAppConfig();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { pathname } = useLocation();
  const year = new Date().getFullYear();
  const label = config.orgName || '';

  // On phones the results screen is a fixed full-bleed map; the footer moves
  // into the list drawer (rendered with `embedded`) instead of sitting below
  // the fold, so the shell copy would only add page scroll here.
  if (!embedded && isMobile && pathname === '/results') return null;

  return (
    <Box
      component="footer"
      sx={{
        // Admin-chosen brand color (secondary), with white text per the design.
        bgcolor: 'secondary.main',
        color: 'secondary.contrastText',
        px: 2.5,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
        fontSize: '0.75rem',
        lineHeight: '20px',
        '@media print': { display: 'none' },
      }}
    >
      <Typography sx={{ fontSize: 'inherit', lineHeight: 'inherit' }}>
        ©
        {' '}
        {year}
        {' '}
        <Box component="span" className="notranslate">{label}</Box>
      </Typography>
      <Link
        href="https://www.schoolmint.com"
        target="_blank"
        rel="noopener noreferrer"
        underline="always"
        sx={{ color: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
      >
        {t('finder.powered_by_schoolmint')}
      </Link>
    </Box>
  );
};

export default Footer;
